import { Stack, Text } from '@repletoryreach/sdk-ui';
import { TextInput, Select, PasswordInput } from '@repletoryreach/sdk-ui/inputs';
import { HTTP_AUTH_TYPES, type HttpAuthType } from './catalog';
import type { JsonObject } from '../mapper';
import './HttpAuthFields.css';

const MUTED = 'var(--rr-color-text-muted, #94a3b8)';
const asStr = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

/**
 * How a request signs in: nothing, a bearer token, a username and password, an
 * API key, a header of the caller's own, or OAuth2 client credentials.
 *
 * These fields were the Web API step's own until 1 October 2026. A live source
 * in the knowledge base makes the same kind of request, so they moved here and
 * both use them. Both store a secret sealed and never show it again (a flow's
 * since 2 October 2026), so each caller passes the names of the secrets already
 * `saved`, which then read "saved" instead of looking empty.
 */
export function HttpAuthFields({ auth, onChange, t, secretHint, saved = [], savedPlaceholder }: {
  auth: JsonObject;
  /** The sign-in settings after a change; undefined when there is no sign-in. */
  onChange: (next: JsonObject | undefined) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
  secretHint?: string;
  saved?: string[];
  savedPlaceholder?: string;
}) {
  const authType: HttpAuthType = (HTTP_AUTH_TYPES as readonly string[]).includes(asStr(auth.type)) ? (asStr(auth.type) as HttpAuthType) : 'none';
  const authText = (key: string) => asStr(auth[key]);
  const setAuth = (changes: Record<string, unknown>) => {
    const next: Record<string, unknown> = { ...auth, ...changes };
    // A new way of signing in starts with nothing saved: the server keeps only what the new way uses.
    if ('type' in changes && changes.type !== auth.type) delete next.saved;
    for (const k of Object.keys(next)) if (next[k] === undefined || next[k] === '') delete next[k];
    onChange(next.type && next.type !== 'none' ? (next as JsonObject) : undefined);
  };
  const hint = secretHint ?? t('flows.builder.field.authSecretHint');
  const kept = (field: string) => (saved.includes(field) && !authText(field) ? savedPlaceholder : undefined);

  return (
    <Stack gap={6}>
      <Select
        label={t('flows.builder.field.auth')}
        description={t(`flows.builder.authHint.${authType}`)}
        value={authType}
        onChange={(e) => setAuth({ type: e.currentTarget.value })}
        data={HTTP_AUTH_TYPES.map((v) => ({ value: v, label: t(`flows.builder.auth.${v}`) }))}
      />
      {authType === 'bearer' && (
        <PasswordInput label={t('flows.builder.field.authToken')} description={hint} placeholder={kept('token')} value={authText('token')} onChange={(e) => setAuth({ token: e.currentTarget.value })} />
      )}
      {authType === 'basic' && (
        <>
          <div className="rr-authfields__pair">
            <TextInput label={t('flows.builder.field.authUsername')} value={authText('username')} onChange={(e) => setAuth({ username: e.currentTarget.value })} />
            <PasswordInput label={t('flows.builder.field.authPassword')} placeholder={kept('password')} value={authText('password')} onChange={(e) => setAuth({ password: e.currentTarget.value })} />
          </div>
          {secretHint && <Text role="caption" c={MUTED}>{hint}</Text>}
        </>
      )}
      {authType === 'api_key' && (
        <>
          <div className="rr-authfields__pair">
            <TextInput label={t('flows.builder.field.authKeyName')} value={authText('key_name')} placeholder={'X-API-Key' /* i18n-ignore: a header name */} list="rr-http-headers" onChange={(e) => setAuth({ key_name: e.currentTarget.value })} />
            <Select
              label={t('flows.builder.field.authKeyIn')}
              value={asStr(auth.in) || 'header'}
              onChange={(e) => setAuth({ in: e.currentTarget.value === 'header' ? undefined : e.currentTarget.value })}
              data={[{ value: 'header', label: t('flows.builder.field.authKeyInHeader') }, { value: 'query', label: t('flows.builder.field.authKeyInQuery') }]}
            />
          </div>
          <PasswordInput label={t('flows.builder.field.authKeyValue')} description={hint} placeholder={kept('key_value')} value={authText('key_value')} onChange={(e) => setAuth({ key_value: e.currentTarget.value })} />
        </>
      )}
      {authType === 'custom_header' && (
        <>
          <div className="rr-authfields__pair">
            <TextInput label={t('flows.builder.field.authHeaderName')} value={authText('header_name')} list="rr-http-headers" onChange={(e) => setAuth({ header_name: e.currentTarget.value })} />
            <PasswordInput label={t('flows.builder.field.authHeaderValue')} placeholder={kept('header_value')} value={authText('header_value')} onChange={(e) => setAuth({ header_value: e.currentTarget.value })} />
          </div>
          <Text role="caption" c={MUTED}>{hint}</Text>
        </>
      )}
      {authType === 'oauth2' && (
        <>
          <TextInput label={t('flows.builder.field.authTokenUrl')} value={authText('token_url')} placeholder="https://auth.example.com/oauth/token" onChange={(e) => setAuth({ token_url: e.currentTarget.value })} />
          <div className="rr-authfields__pair">
            <TextInput label={t('flows.builder.field.authClientId')} value={authText('client_id')} onChange={(e) => setAuth({ client_id: e.currentTarget.value })} />
            <PasswordInput label={t('flows.builder.field.authClientSecret')} placeholder={kept('client_secret')} value={authText('client_secret')} onChange={(e) => setAuth({ client_secret: e.currentTarget.value })} />
          </div>
          <div className="rr-authfields__pair">
            <TextInput label={t('flows.builder.field.authScope')} value={authText('scope')} onChange={(e) => setAuth({ scope: e.currentTarget.value })} />
            <Select
              label={t('flows.builder.field.authClientAuth')}
              value={asStr(auth.client_auth) || 'body'}
              onChange={(e) => setAuth({ client_auth: e.currentTarget.value === 'body' ? undefined : e.currentTarget.value })}
              data={[{ value: 'body', label: t('flows.builder.field.authClientAuthBody') }, { value: 'basic', label: t('flows.builder.field.authClientAuthBasic') }]}
            />
          </div>
          <Text role="caption" c={MUTED}>{hint}</Text>
        </>
      )}
    </Stack>
  );
}
